import React, { useRef, useState, useEffect } from 'react';
import type { SchedulerEvent } from '../../types';
import clsx from 'clsx';
import { DAY_WIDTH, getDateFromPixel, getPixelFromDate } from '../utils/dateUtils';
import { GripVertical } from 'react-bootstrap-icons';
import { useScheduler } from './SchedulerContext';
import { addDays, startOfDay, isAfter, isBefore, isSameDay } from 'date-fns';
import moment from 'moment';
import { useAppSelector } from '../../app/hooks';
import { FormatInvoiceDecimalNumber } from '../../common/timeLiveFunction';

interface Props {
  event: SchedulerEvent;
  containerRef: React.RefObject<HTMLDivElement>;
  UpdateEventResize?: (event: SchedulerEvent) => void;
  lane?: number;
  totalLanes?: number;
}

const normalizeDate = (date: Date): Date => {
  return startOfDay(new Date(date));
}

export const EventItem: React.FC<Props> = ({ event, containerRef, UpdateEventResize, lane = 0, totalLanes = 1 }) => {
  const { cultureInfoName, decimalPlaces } = useAppSelector((state) => state?.globalState);
  const { startDate, viewMode, handleEventUpdate, onEventClick, onEventContextMenu } = useScheduler();
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState<'start' | 'end' | null>(null);
  const [hasResized, setHasResized] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);
  const [localStart, setLocalStart] = useState(event.start);
  const [localEnd, setLocalEnd] = useState(event.end);
  const eventRef = useRef<HTMLDivElement>(null);

  const EVENT_HEIGHT = 37;
  const EVENT_GAP = 2;
  const topOffset = lane * (EVENT_HEIGHT + EVENT_GAP);

  useEffect(() => {
    if (!isDragging && !isResizing) {
      const startChanged = event.start.getTime() !== localStart.getTime();
      const endChanged = event.end.getTime() !== localEnd.getTime();

      if (startChanged || endChanged) {
        setLocalStart(event.start);
        setLocalEnd(event.end);
      }
    }
  }, [event.start, event.end, isDragging, isResizing]);

  const normalizedStart = event.types === 'milestone' ? normalizeDate(localStart) : localStart;
  const getCurrentDatePosition = () => {
    const viewStart = startOfDay(startDate);
    const eventStartDate = startOfDay(normalizedStart);
    const eventEndDate = startOfDay(localEnd);

    if (isBefore(eventStartDate, viewStart) && (isAfter(eventEndDate, viewStart) || isSameDay(eventEndDate, viewStart))) {

      const daysFromStart = Math.floor(
        (viewStart.getTime() - eventStartDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      return daysFromStart * DAY_WIDTH;
    }

    return 0;
  };

  const textOffset = getCurrentDatePosition();
  const left = getPixelFromDate(normalizedStart, startDate, viewMode);
  const width = event.types === 'milestone' ? DAY_WIDTH - 2 : Math.max(getPixelFromDate(addDays(localEnd, 1), startDate, viewMode) - left, 10);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (e.button !== 0) return;
    if (event.types === 'allocation') return;

    setIsDragging(true);
    const startX = e.clientX;
    const initialLeft = left;
    let hasMoved = false;
    let latestStart = localStart;
    let latestEnd = localEnd;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      if (Math.abs(deltaX) > 5) {
        hasMoved = true;
      }
      const newLeft = initialLeft + deltaX;

      const newStartDate = getDateFromPixel(newLeft, startDate, viewMode);
      const duration = event.end.getTime() - event.start.getTime();
      const newEndDate = new Date(newStartDate.getTime() + duration);
      latestStart = newStartDate;
      latestEnd = newEndDate;
      setLocalStart(newStartDate);
      setLocalEnd(newEndDate);
    };

    const onMouseUp = () => {
      setIsDragging(false);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      if (hasMoved) {
        setHasDragged(true);
        const updatedEvent = {
          ...event,
          start: startOfDay(latestStart),
          end: startOfDay(latestEnd)
        };

        if (UpdateEventResize) {
          UpdateEventResize(updatedEvent);
        }

        handleEventUpdate(updatedEvent);
        setTimeout(() => setHasDragged(false), 100);
      } else {
        setLocalStart(event.start);
        setLocalEnd(event.end);
      }
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const latestEndRef = useRef(event.end);
  const handleResizeStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    // if (event.types !== 'task') return;
    if (event.types === 'allocation' || event.types === 'milestone' || event.types === 'project') return;

    setIsResizing('end');
    setHasResized(true);

    const startX = e.clientX;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const newWidth = width + deltaX;
      const totalDays = Math.round(newWidth / DAY_WIDTH);
      const safeDays = Math.max(1, totalDays);
      const newEndDate = addDays(startOfDay(localStart), safeDays - 1);

      setLocalEnd(newEndDate);
      latestEndRef.current = newEndDate;
    };

    const onMouseUp = () => {
      setIsResizing(null);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);

      const finalEnd = latestEndRef.current;

      if (UpdateEventResize) {
        UpdateEventResize({
          ...event,
          start: startOfDay(localStart),
          end: startOfDay(finalEnd)
        });
      }

      handleEventUpdate({ ...event, end: finalEnd });
      setTimeout(() => setHasResized(false), 100);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (hasResized || hasDragged) {
      return;
    }

    if (onEventClick) {
      onEventClick(event);
    }
  };

  const hexToRgba = (hex: string, opacity: number) => {
    const cleanHex = hex.replace('#', '');
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  const tooltipProps = (event.types === 'task' || event.types === 'taskTeam' || event.types === 'allocation') ? {
    'data-tooltip-id': 'scheduler-event-tooltip',
    'data-tooltip-html': `<div style="line-height: 1.5;"><div style="font-weight: bold; margin-bottom: 4px;">${event.title !== 'Null' ? `<div style="font-weight: bold; margin-bottom: 4px;"> ${event.title} </div>` : ''}</div><div>${(event.types === 'allocation' && event.percentage) ? `Total Hours: ${FormatInvoiceDecimalNumber(cultureInfoName.toString(), event?.TotalHours, 2)}h` : `${event.types === 'allocation' ? '' : `Assigned: ${FormatInvoiceDecimalNumber(cultureInfoName.toString(), event.allocationHoursPerDay, 2) ?? 0} h/d`}`}</div></div>`
  } : {};

  if (event.types === 'milestone') {
    return (
      <div
        ref={eventRef}
        onClick={(e) => { e.stopPropagation(); onEventClick?.(event); }}
        data-tooltip-id="scheduler-event-tooltip"
        data-tooltip-html={`<div style="line-height: 1.5;"><div style="font-weight: bold;">${event.title}</div></div>`}
        className="position-absolute d-flex align-items-center justify-content-center user-select-none"
        style={{
          left: `${left + 1}px`,
          width: `${width}px`,
          height: '35px',
          top: '4px',
          backgroundColor: '#67266D',
          opacity: 0.5,
          border: 'none',
          cursor: 'pointer',
          zIndex: 50,
          fontSize: '0.7rem',
          fontWeight: '500',
          textAlign: 'center',
          padding: '0 4px',
          boxSizing: 'border-box',
          color: 'white'
        }}
        title={event.title}
      >
        <span className="text-truncate w-100 text-white">
          {event.title}
        </span>
      </div>
    );
  }

  if (event.types === 'timeOff') {
    return (
      <div
        ref={eventRef}
        onClick={(e) => { e.stopPropagation(); onEventClick?.(event); }}
        data-tooltip-id="scheduler-event-tooltip"
        data-tooltip-html={`<div style="line-height: 1.5;"><div style="font-weight: bold;">${moment(event.start).format('DD MMM yy')} - ${moment(event.end).format('DD MMM yy')}</div><div style="text-align: center;">Time Off</div></div>`}
        className="position-absolute d-flex align-items-center justify-content-center user-select-none"
        style={{
          left: `${left - 20}px`,
          width: `${width}px`,
          height: `${EVENT_HEIGHT}px`,
          top: `${5 + topOffset}px`,
          // height: '37px',
          // top: '5px',
          // backgroundColor:'#FAD1DF',
          // opacity: 0.5,
          cursor: 'pointer',
          zIndex: 5,
          fontSize: '0.75rem',
          fontWeight: '500',
          textAlign: 'center',
          padding: '0 4px',
          boxSizing: 'border-box',
          color: '#991b1b'
        }}
        title={event.title}
      >
        {/* <span className="text-truncate w-100" style={{ fontWeight: 'bold' }}>
          {event.title}
        </span> */}
      </div>
    );
  }

  return (
    <div
      ref={eventRef}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      onContextMenu={(e) => { e.preventDefault(); onEventContextMenu?.(event, e); }}
      className={clsx("position-absolute overflow-hidden d-flex align-items-center user-select-none", isDragging ? "border border-info" : "", !event.color && "bg-primary")}
      style={{
        left: `${left}px`,
        width: `${width}px`,
        height: `${EVENT_HEIGHT}px`,
        top: `${5 + topOffset}px`,
        background: (event.types === 'project') ? event.color : (event.types === 'allocation') ? `linear-gradient(0deg, ${hexToRgba(event.color ?? '#8bc34a', 0.2)}, ${hexToRgba(event.color ?? '#8bc34a', 0.2)}), #ffffff` : `linear-gradient(0deg, rgba(12, 102, 228, 0.1), rgba(12, 102, 228, 0.1)), #ffffff`,
        //backgroundColor: (event.types === 'project') ? event.color : (event.types === 'allocation') ? hexToRgba(event.color ?? '#8bc34a', 0.2) : 'rgba(12, 102, 228, 0.1)',
        borderBottom: (event.types === 'project') ? '3px solid #1e3a8a' : (event.types === 'projectEmployee') ? '3px solid #E8F3DB' : (event.types === 'allocation') ? `3px solid ${event.color}` : '3px solid #0C66E4',
        transition: isDragging || isResizing ? 'none' : 'left 0.1s, width 0.1s',
        cursor: isDragging ? 'grabbing' : 'pointer',
        fontSize: '0.75rem',
        zIndex: isDragging ? 1050 : 40
      }}
    >
      <div  {...tooltipProps} className="px-2 d-flex flex-column justify-content-center" style={{ marginLeft: `${textOffset}px`, transition: 'margin-left 0.3s ease' }}>
        <div className="d-flex align-items-center text-truncate">
          {width > 30 && event.types != "allocation" && event.types != 'project' && (
            <GripVertical size={12} className="me-1 opacity-50 flex-shrink-0" />
          )}
          <span className="fw-bold text-truncate text-dark">{event.title != 'Null' && event.title}</span>
        </div>
        {!(event.types === 'project' || event.types === 'allocation') && (
          <div className="d-flex align-items-start ms-4">
            <span className="fw-bold text-truncate">
              <strong>{FormatInvoiceDecimalNumber(cultureInfoName.toString(), event.allocationHoursPerDay, 2) ?? 0} h/d </strong>, {FormatInvoiceDecimalNumber(cultureInfoName.toString(), event.HoursPerDay, 2)} h
            </span>
          </div>
        )}
        {(event?.types === 'allocation' && event?.percentage) && (
          <div className="d-flex align-items-start">
            <span className="fw-bold text-truncate">
              {event?.HoursOff > 0 ?
                `${FormatInvoiceDecimalNumber(cultureInfoName.toString(), event?.TotalHours, 2)} + ${event?.HoursOff}h `
                : `${FormatInvoiceDecimalNumber(cultureInfoName.toString(), event?.TotalHours, 2)}h`}
            </span>
          </div>
        )}
      </div>
      {(event.types === 'task' || event.types === 'taskTeam') && (
        <div
          onMouseDown={handleResizeStart}
          className="position-absolute top-0 bottom-0 end-0"
          style={{ width: '8px', cursor: 'col-resize', zIndex: 20 }}
        />
      )}
    </div>
  );
};